/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AddEquipmentKind } from 'components/structure-page/modals/add-equipment-modal-config';
import {
  ActiveItemType,
  ActiveTabType,
  ActiveTipSequenceType,
  BulkValidSystemsPaginationType,
  ComparisonFeatureType,
  ComparisonTableDataType,
  CompletedTaskType,
  EasyModalType,
  MatchingAirHandlersModeType,
  PageProgressType,
  RebateType,
  SliderRangesType,
  SliderValuesType,
  SystemFiltersType,
  ZoneSliderValuesType
} from 'data/types';
import { CombinationFilter } from 'components/common/combination-filter-controls';
import { StructureStepRef } from 'utils/completed-steps-helpers';
import { RadialDuctModeKeyType } from 'utils/ducting-products';
import {
  defaultGalleryFilters,
  GalleryFilterState,
  GallerySortOption
} from 'utils/system-gallery-filters';
import { DEFAULT_SYSTEM_SORT, SystemSortOption } from 'utils/system-sort';
import { loadDismissedTips, loadTipsSkipped } from 'utils/tips';
import { ZoneDuctModesMapType } from 'utils/zone-duct-modes';
import {
  defaultEquipmentFilterMemory,
  defaultEquipmentFilters,
  EquipmentFilterMemoryState,
  EquipmentFilterState
} from './equipment-filter-types';
import {
  DuctedFilterSelectionSource,
  enumFilterValuesChanged,
  enumFilterValuesOf,
  resolveContextSeededEnumFilters,
  withSeededEnumFilter
} from './seeded-enum-filters';
import { resetDuctedPaging } from './ducted-paging';
import {
  tonnageFilterMatchesSeed,
  tonnageSeedKeyOf,
  withSeededTonnageFilter
} from './tonnage-filter-seed';
import { TonnageSeedRange } from './tonnage-filter-seed-types';
import { DuctedPairPick } from './ducted-pair-pick-types';

export const DEFAULT_GALLERY_PAGE_SIZE = 25;

export type FailedValidSystemsSource = 'full' | 'gallery' | 'options';

// One System Type chip selection, scoped to a Choose Systems result set
// (AHA-6681 kept/alternatives filter split): the same code can be selected
// for the kept-constrained set, the alternatives set, or both — each is its
// own chip. `resultSet` mirrors DuctedResultSet (inlined to keep ui-slice
// out of the ducted-compatibility import graph).
export type DuctedSystemTypeSelection = {
  code: string;
  resultSet: 'kept' | 'alternatives';
};

// Who last authored a shared fuel/chassis/orientation filter value — see
// seeded-enum-filters.ts for the contract; re-exported here because the ui
// barrel is where the row components import it from.
export type { DuctedFilterSelectionSource } from './seeded-enum-filters';

// Applied ducted compatible-combinations filter state (AHA-6681 CS-1) —
// one shared selection across all eligible ducted systems, mirroring the
// single filter section on Choose Systems. An empty systemTypeSelections
// list means ALL RECOMMENDED everywhere (the All-Types browse-everything
// default is gone since sub-build 3); while ANY selection is active, each
// result set renders/fetches only its own scoped codes (strict split — a
// set with no scoped selection shows zero rows). `resetResultsKey` records
// which LES-4 results context the System Type selection was last reset
// for: the reset (back to the empty all-recommended default) is applied
// once per results context and never re-applied on refetch or after the
// user edits/clears the selection.
export type DuctedFilterState = {
  filters: CombinationFilter[];
  systemTypeSelections: DuctedSystemTypeSelection[];
  // AHA-6796 Fuel chips: ONE shared multi-select fuel union across all
  // eligible systems and both result sets (a fuel is a property of the code
  // itself — no per-set identity). Empty = all fuels. Token union mirrors
  // DuctedFuelToken (inlined like resultSet, keeping ui-slice out of the
  // utils import graph). Seeded per results context by
  // resetDuctedSystemTypeCodes (['lp'] off a propane furnace), so a user
  // clear survives every refetch/remount within the context.
  fuelSelections: Array<'ng' | 'lp' | 'o' | 'e'>;
  // Source flags for the shared fuel/chassis/orientation values (AHA-6796
  // EE rows — see DuctedFilterSelectionSource): a 'manual' value survives
  // the once-per-context reset (fuel pruned to the new bound's fuels,
  // chassis and orientation kept verbatim); 'seed' re-seeds exactly as
  // before.
  fuelSelectionsSource: DuctedFilterSelectionSource;
  chassisFilterSource: DuctedFilterSelectionSource;
  orientationFilterSource: DuctedFilterSelectionSource;
  pageNumber: number;
  pageSize: number;
  // False until the user clicks "Show more": the rail shows each system
  // type's lead cards (kept pairing and full replacement); true reveals the
  // loaded rows beyond them (and further clicks page). Reset with pageNumber
  // on every result-set change (resetDuctedPaging).
  tailRevealed: boolean;
  resetResultsKey: string | null;
  // AHA-6796 bands T3: the sizing-bands relax override. true (default) =
  // let the server apply the band constraint; false = the user clicked
  // "Show all sizes" — the fetches send applySizingBands: false. Redux-
  // session-only (a reload re-arms bands); re-armed once per results
  // context by resetDuctedSystemTypeCodes.
  applySizingBands: boolean;
  tonnageSeedKey: string | null;
  // Equipment Matching filter: true = show only the kept-constrained
  // ("Pairs with Customer's Equipment") result set; the alternatives set is
  // hidden from the rail, the counts and the chip universes. Display-only —
  // never rides a fetch body. Cleared on every new results context.
  equipmentMatchingOnly: boolean;
};

export const defaultDuctedFilters: DuctedFilterState = {
  filters: [],
  systemTypeSelections: [],
  fuelSelections: [],
  fuelSelectionsSource: 'seed',
  chassisFilterSource: 'seed',
  orientationFilterSource: 'seed',
  pageNumber: 1,
  pageSize: 25,
  tailRevealed: false,
  resetResultsKey: null,
  applySizingBands: true,
  tonnageSeedKey: null,
  equipmentMatchingOnly: false
};

// The pnet spec-filter field the chassis seed targets (AHA-6796): the
// existing faceted enum served by the compatible-combinations registry.
export const CABINET_CHASSIS_SIZE_FILTER_FIELD = 'cabinetChassisSize';

// The pnet spec-filter field the orientation seed targets: the existing
// faceted enum (Upflow / Downflow / Horizontal, resolved from the spec
// tables' airflow bits) served by the compatible-combinations registry.
export const ORIENTATION_FILTER_FIELD = 'orientation';

// The chassis-seed filter composition (AHA-6796): the carried spec filters
// minus any cabinetChassisSize entry, plus the seeded entry when sizes
// exist — see withSeededEnumFilter for the shared contract.
export function withSeededChassisFilter(
  filters: CombinationFilter[],
  seedChassisSizes: string[]
): CombinationFilter[] {
  return withSeededEnumFilter(
    filters,
    CABINET_CHASSIS_SIZE_FILTER_FIELD,
    seedChassisSizes
  );
}

// The orientation-seed filter composition: the same contract as the chassis
// seed, on the orientation facet — the existing furnace / air handler /
// coil's airflow direction is what the ductwork dictates, so the
// replacement is held to it until the user says otherwise.
export function withSeededOrientationFilter(
  filters: CombinationFilter[],
  seedOrientations: string[]
): CombinationFilter[] {
  return withSeededEnumFilter(
    filters,
    ORIENTATION_FILTER_FIELD,
    seedOrientations
  );
}

// The context-reset FUEL composition (AHA-6796 EE rows): what
// fuelSelections becomes when a new results context lands. Seed-sourced →
// the fresh seed (exactly the pre-source behavior); manual-sourced → the
// user's selection PRUNED to the fuels the new FULL universe actually
// offers (the no-invisible-filter rule — the chip rows render fuels
// present in the eligible catalog universe since AHA-6796, so an
// out-of-universe survivor would be unclearable; a manual ['ng'] on an
// LP-derived project survives because ng is in the universe even when the
// recommended bound is lp-only). Shared by the resetDuctedSystemTypeCodes
// reducer and the pre-warm default-params builder so the two cannot
// diverge (the withSeededChassisFilter precedent). Reference-stable when
// nothing changes.
export function resolveContextFuelSelections(
  current: Array<'ng' | 'lp' | 'o' | 'e'>,
  source: DuctedFilterSelectionSource,
  seedFuels: Array<'ng' | 'lp' | 'o' | 'e'>,
  universeFuels: Array<'ng' | 'lp' | 'o' | 'e'>
): Array<'ng' | 'lp' | 'o' | 'e'> {
  if (source !== 'manual') return seedFuels;
  const universe = new Set(universeFuels);
  const pruned = current.filter((fuel) => universe.has(fuel));
  return pruned.length === current.length ? current : pruned;
}

// The context-reset CHASSIS composition (AHA-6796 EE rows): seed-sourced →
// the seeded upsert; manual-sourced → the carried filters untouched — see
// resolveContextSeededEnumFilters for the shared contract. Shared with the
// pre-warm builder for hash parity.
export function resolveContextChassisFilters(
  filters: CombinationFilter[],
  source: DuctedFilterSelectionSource,
  seedChassisSizes: string[]
): CombinationFilter[] {
  return resolveContextSeededEnumFilters(
    filters,
    CABINET_CHASSIS_SIZE_FILTER_FIELD,
    source,
    seedChassisSizes
  );
}

// The context-reset ORIENTATION composition: same contract as the chassis
// composition, on the orientation facet. Shared with the pre-warm builder
// for hash parity.
export function resolveContextOrientationFilters(
  filters: CombinationFilter[],
  source: DuctedFilterSelectionSource,
  seedOrientations: string[]
): CombinationFilter[] {
  return resolveContextSeededEnumFilters(
    filters,
    ORIENTATION_FILTER_FIELD,
    source,
    seedOrientations
  );
}

// A payload whose entry for `field` DIFFERS (by content, not order) from
// the current one is a user statement about that field (AHA-6796 EE rows).
function seededEnumFilterEdited(
  current: CombinationFilter[],
  next: CombinationFilter[],
  field: string
): boolean {
  return enumFilterValuesChanged(
    enumFilterValuesOf(current, field),
    enumFilterValuesOf(next, field)
  );
}

// AHA-6796 gate telemetry: set only when a notify-mode confidence gate is
// continued past, so saveQuoteInfo can stamp the finalized quoteData.
// Cleared on every clean gate pass and after a successful save batch —
// stale telemetry must never ride a later healthy finalization.
export type ConfidenceGateTelemetryType = {
  gated: true;
  mode: 'notify';
  continuedAnyway: true;
};

export interface UIState {
  activeTab: ActiveTabType;
  previousTab: ActiveTabType | null;
  activeAdminTab: number;
  pageProgress: PageProgressType;
  activeItem: ActiveItemType | null;
  showPropertySummary: boolean;
  googleApiLoaded: boolean;
  sliderValues: SliderValuesType;
  defaultSliderValues: SliderValuesType;
  zoneSliderValues: { [zoneId: string]: ZoneSliderValuesType };
  sliderRanges: SliderRangesType;
  systemFilters: SystemFiltersType;
  galleryFilters: GalleryFilterState;
  ductedFilters: DuctedFilterState;
  equipmentFilters: EquipmentFilterState;
  equipmentFilterMemory: EquipmentFilterMemoryState;
  equipmentConfigSelectionSignature: string | null;
  matchingAirHandlersMode: MatchingAirHandlersModeType;
  gallerySort: GallerySortOption;
  systemSort: SystemSortOption;
  galleryPage: number;
  galleryPageSize: number;
  galleryPagination: BulkValidSystemsPaginationType | null;
  showSettingsPanel: boolean;

  showChatPanel: boolean;
  showDebugMenu: boolean;
  preferredAirHandlerErrors: { zoneId: string; error: string }[];
  activeAirHandlerPicker: string | null;
  activeCondenserPicker: string | null;
  activeAccessoryDrawer: string | null;
  equipmentModalKind: AddEquipmentKind | null;
  editingEquipmentComponentId: string | null;
  equipmentModalTargetSystemId: string | null;
  debugMode: boolean;
  reproducingErrorLogId: number | null;
  developerMode: boolean;
  rebatesForModal: RebateType[];
  estimatingStructure: boolean;
  analyzingStructureImages: boolean;
  easyMode: boolean;
  showZones: boolean;
  activeLevelIndex: number;
  activeLevelDetailIndex: number;
  activeSubStepIndex: number;
  easyModalsFinished: EasyModalType[];
  comparisonTableData: ComparisonTableDataType | null;
  globalComparisonFeatures: ComparisonFeatureType[];
  quoteIDsToSend: string[];
  layoutImagesSkipped: boolean;
  squareFootageWarningShown: boolean;
  taskModal: {
    isOpen: boolean;
    tasks: CompletedTaskType[];
    title: string;
    flyToTarget: { x: number; y: number };
  };
  activeTipSequence: ActiveTipSequenceType | null;
  tipSequenceProgress: Record<string, number>;
  dismissedTips: string[];
  tipsSkipped: boolean;
  manuallyCompletedSteps: string[];
  visitedStructureSteps: string[];
  completedStepsLoaded: boolean;
  pendingImportCompletedSteps: StructureStepRef[] | null;
  skipConfigurationReset: boolean;
  categoriesWithUnselectedRecommendations: string[];
  activeStructureStepId: string;
  activeProjectStepId: string;
  activeExistingEquipmentStepId: string;
  activeConfigurationStepId: string;
  activeConfigurationStepConfigId: string | null;
  fetchingCondenserIds: string[];
  fetchingFullConfigIds: string[];
  failedValidSystemsConfigSources: Record<string, FailedValidSystemsSource[]>;
  zoneDuctModes: ZoneDuctModesMapType;
  confidenceGateTelemetry: ConfidenceGateTelemetryType | null;
  hoveredConcealedZoneId: string | null;
  ductedPairPicks: Record<string, DuctedPairPick>;
}

export const defaultUIState: UIState = {
  activeTab: 'project',
  previousTab: null,
  activeAdminTab: 0,
  pageProgress: {
    project: 0,
    existingEquipment: 0,
    equipmentPreferences: 0,
    structure: 0,
    configurations: 0,
    chooseSystems: 0,
    accessories: 0,
    quote: 0
  },
  activeItem: null,
  showPropertySummary: false,
  googleApiLoaded: false,
  sliderValues: {
    heatingComfort: 7,
    coolingComfort: 7,
    aesthetics: 3,
    installComplexity: 3,
    efficiency: 28
  },
  defaultSliderValues: {
    heatingComfort: 7,
    coolingComfort: 7,
    aesthetics: 3,
    installComplexity: 3,
    efficiency: 28
  },
  zoneSliderValues: {},
  sliderRanges: {
    heatingComfort: { min: 1, max: 7 },
    coolingComfort: { min: 1, max: 7 },
    aesthetics: { min: 1, max: 5 },
    installComplexity: { min: 1, max: 5 },
    efficiency: { min: 16, max: 38 }
  },
  systemFilters: {
    inStock: true,
    belowBudget: false,
    selectedAirHandlers: true,
    isOnSale: false
  },
  galleryFilters: defaultGalleryFilters,
  ductedFilters: defaultDuctedFilters,
  equipmentFilters: defaultEquipmentFilters,
  equipmentFilterMemory: defaultEquipmentFilterMemory,
  equipmentConfigSelectionSignature: null,
  matchingAirHandlersMode: 'auto',
  gallerySort: 'score',
  systemSort: DEFAULT_SYSTEM_SORT,
  galleryPage: 0,
  galleryPageSize: DEFAULT_GALLERY_PAGE_SIZE,
  galleryPagination: null,
  showSettingsPanel: false,

  showChatPanel: false,
  showDebugMenu: false,
  preferredAirHandlerErrors: [],
  activeAirHandlerPicker: null,
  activeCondenserPicker: null,
  activeAccessoryDrawer: null,
  equipmentModalKind: null,
  editingEquipmentComponentId: null,
  equipmentModalTargetSystemId: null,
  debugMode: false,
  reproducingErrorLogId: null,
  developerMode: false,
  rebatesForModal: [],
  estimatingStructure: false,
  analyzingStructureImages: false,
  easyMode: false,
  showZones: false,
  activeLevelIndex: 0,
  activeLevelDetailIndex: 0,
  activeSubStepIndex: 0,
  easyModalsFinished: [],
  comparisonTableData: null,
  globalComparisonFeatures: [],
  quoteIDsToSend: [],
  layoutImagesSkipped: false,
  squareFootageWarningShown: false,
  taskModal: {
    isOpen: false,
    tasks: [],
    title: 'Completing Tasks',
    flyToTarget: { x: 100, y: 100 }
  },
  activeTipSequence: null,
  tipSequenceProgress: {},
  dismissedTips: loadDismissedTips(),
  tipsSkipped: loadTipsSkipped(),
  manuallyCompletedSteps: [],
  visitedStructureSteps: [],
  completedStepsLoaded: false,
  pendingImportCompletedSteps: null,
  skipConfigurationReset: false,
  categoriesWithUnselectedRecommendations: [],
  activeStructureStepId: 'confirm_details',
  activeProjectStepId: 'get_started',
  activeExistingEquipmentStepId: 'have',
  activeConfigurationStepId: 'condensers',
  activeConfigurationStepConfigId: null,
  fetchingCondenserIds: [],
  fetchingFullConfigIds: [],
  failedValidSystemsConfigSources: {},
  zoneDuctModes: {},
  confidenceGateTelemetry: null,
  hoveredConcealedZoneId: null,
  ductedPairPicks: {}
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState: defaultUIState,
  reducers: {
    setActiveTab: (state, action: PayloadAction<ActiveTabType>) => {
      if (state.activeTab !== action.payload) {
        state.previousTab = state.activeTab;
      }
      state.activeTab = action.payload;
    },
    setActiveAdminTab: (state, action: PayloadAction<number>) => {
      state.activeAdminTab = action.payload;
    },
    setGoogleApiLoaded: (state, action: PayloadAction<boolean>) => {
      state.googleApiLoaded = action.payload;
    },
    setPageProgress: (state, action: PayloadAction<PageProgressType>) => {
      state.pageProgress = action.payload;
    },
    setActiveItem: (state, action: PayloadAction<ActiveItemType | null>) => {
      state.activeItem = action.payload;
    },
    setShowPropertySummary: (state, action: PayloadAction<boolean>) => {
      state.showPropertySummary = action.payload;
    },
    setSliderValues: (state, action: PayloadAction<SliderValuesType>) => {
      state.sliderValues = action.payload;
    },
    setDefaultSliderValues: (
      state,
      action: PayloadAction<SliderValuesType>
    ) => {
      state.defaultSliderValues = action.payload;
    },
    setZoneSliderValues: (
      state,
      action: PayloadAction<{ [zoneId: string]: ZoneSliderValuesType }>
    ) => {
      state.zoneSliderValues = action.payload;
    },
    setSliderRanges: (state, action: PayloadAction<SliderRangesType>) => {
      state.sliderRanges = action.payload;
    },
    setSystemFilters: (state, action: PayloadAction<SystemFiltersType>) => {
      state.systemFilters = action.payload;
    },
    setGalleryFilters: (state, action: PayloadAction<GalleryFilterState>) => {
      state.galleryFilters = action.payload;
    },
    updateGalleryFilter: (
      state,
      action: PayloadAction<Partial<GalleryFilterState>>
    ) => {
      state.galleryFilters = { ...state.galleryFilters, ...action.payload };
    },
    // Filter/code changes reset pageNumber (same behavior as the CCB modal).
    // A payload whose cabinetChassisSize (or orientation) entry DIFFERS from
    // the current one is a user statement about that seed (AHA-6796 EE
    // rows) — both the EE row and the CS facet control write through here,
    // so this one rule stamps the manual source for both; other-field edits
    // leave the sources alone.
    setDuctedCombinationFilters: (
      state,
      action: PayloadAction<CombinationFilter[]>
    ) => {
      if (
        seededEnumFilterEdited(
          state.ductedFilters.filters,
          action.payload,
          CABINET_CHASSIS_SIZE_FILTER_FIELD
        )
      ) {
        state.ductedFilters.chassisFilterSource = 'manual';
      }
      if (
        seededEnumFilterEdited(
          state.ductedFilters.filters,
          action.payload,
          ORIENTATION_FILTER_FIELD
        )
      ) {
        state.ductedFilters.orientationFilterSource = 'manual';
      }
      state.ductedFilters.filters = action.payload;
      resetDuctedPaging(state.ductedFilters);
    },
    setDuctedSystemTypeSelections: (
      state,
      action: PayloadAction<DuctedSystemTypeSelection[]>
    ) => {
      state.ductedFilters.systemTypeSelections = action.payload;
      resetDuctedPaging(state.ductedFilters);
    },
    clearDuctedChassisFilter: (state) => {
      const withoutChassis = state.ductedFilters.filters.filter(
        (filter) => filter.field !== CABINET_CHASSIS_SIZE_FILTER_FIELD
      );
      if (withoutChassis.length !== state.ductedFilters.filters.length) {
        state.ductedFilters.filters = withoutChassis;
        resetDuctedPaging(state.ductedFilters);
      }
      state.ductedFilters.chassisFilterSource = 'seed';
    },
    // Reset the System Type selection to the empty all-recommended default
    // when the results context changes (AHA-6681 CS-1; empty = ALL
    // RECOMMENDED since sub-build 3): applied at most once per results
    // context — a repeat dispatch for an already-reset key is a no-op, so
    // remounts never clobber the user's edits. seedFuels (AHA-6796 Fuel
    // chips) and seedChassisSizes (AHA-6796 chassis seed) ride the same
    // once-per-context cadence: the dispatch site derives them from the
    // eligible systems (['lp'] off a propane furnace; the union of the
    // uploaded components' cabinetChassisSize letters), and the no-op on a
    // repeated key is what lets a user clear survive refetches/remounts
    // within the context. MANUAL-sourced fuel/chassis/orientation values
    // survive even a NEW context (AHA-6796 EE rows — the resolveContext*
    // composition helpers): fuel pruned to universeFuels (the new context's
    // FULL eligible-universe fuel vocabulary, derived at the dispatch site
    // from the same ductedUniverseCodes union as seedFuels), chassis and
    // orientation kept verbatim. seedOrientations rides the same cadence:
    // the existing furnace / air handler / coil's airflow direction
    // (Upflow / Downflow / Horizontal) the ductwork dictates.
    resetDuctedSystemTypeCodes: (
      state,
      action: PayloadAction<{
        resultsKey: string;
        seedFuels: Array<'ng' | 'lp' | 'o' | 'e'>;
        seedChassisSizes: string[];
        seedOrientations: string[];
        universeFuels: Array<'ng' | 'lp' | 'o' | 'e'>;
      }>
    ) => {
      if (state.ductedFilters.resetResultsKey === action.payload.resultsKey) {
        return;
      }
      state.ductedFilters.resetResultsKey = action.payload.resultsKey;
      state.ductedFilters.systemTypeSelections = [];
      state.ductedFilters.fuelSelections = resolveContextFuelSelections(
        state.ductedFilters.fuelSelections,
        state.ductedFilters.fuelSelectionsSource,
        action.payload.seedFuels,
        action.payload.universeFuels
      );
      // Chassis + orientation seeds (AHA-6796): upsert ONLY the
      // cabinetChassisSize and orientation enum entries — every other
      // carried spec filter survives the reset (the documented
      // ducted-fetch-params contract). Shared composition with the pre-warm
      // builder (resolveContext*Filters, chassis then orientation) keeps
      // hash parity, manual preservation included.
      state.ductedFilters.filters = resolveContextOrientationFilters(
        resolveContextChassisFilters(
          state.ductedFilters.filters,
          state.ductedFilters.chassisFilterSource,
          action.payload.seedChassisSizes
        ),
        state.ductedFilters.orientationFilterSource,
        action.payload.seedOrientations
      );
      resetDuctedPaging(state.ductedFilters);
      // A new results context re-applies bands (once per context, same
      // cadence as the selection reset); within a context the relax
      // override persists across filter/chip/page changes.
      state.ductedFilters.applySizingBands = true;
      state.ductedFilters.equipmentMatchingOnly = false;
    },
    setDuctedEquipmentMatchingOnly: (state, action: PayloadAction<boolean>) => {
      state.ductedFilters.equipmentMatchingOnly = action.payload;
      resetDuctedPaging(state.ductedFilters);
    },
    // Fuel chip selection (AHA-6796): the result set changes, so pageNumber
    // resets (mirrors setDuctedCombinationFilters). Every write here is a
    // user gesture (EE row or CS chip row) — stamp the manual source so
    // context resets preserve it (a clear included: [] with source
    // 'manual' stays cleared).
    setDuctedFuelSelections: (
      state,
      action: PayloadAction<Array<'ng' | 'lp' | 'o' | 'e'>>
    ) => {
      state.ductedFilters.fuelSelections = action.payload;
      state.ductedFilters.fuelSelectionsSource = 'manual';
      resetDuctedPaging(state.ductedFilters);
    },
    // Sizing-bands relax override (AHA-6796 bands T3): the result set
    // changes, so pageNumber resets (mirrors setDuctedCombinationFilters).
    setDuctedApplySizingBands: (state, action: PayloadAction<boolean>) => {
      state.ductedFilters.applySizingBands = action.payload;
      resetDuctedPaging(state.ductedFilters);
    },
    applyDuctedTonnageSeed: (
      state,
      action: PayloadAction<TonnageSeedRange | null>
    ) => {
      const seed = action.payload;
      const seedKey = tonnageSeedKeyOf(seed);
      if (seed === null) {
        state.ductedFilters.tonnageSeedKey = null;
        return;
      }
      if (state.ductedFilters.tonnageSeedKey === seedKey) return;
      state.ductedFilters.tonnageSeedKey = seedKey;
      if (tonnageFilterMatchesSeed(state.ductedFilters.filters, seed)) return;
      state.ductedFilters.filters = withSeededTonnageFilter(
        state.ductedFilters.filters,
        seed
      );
      resetDuctedPaging(state.ductedFilters);
    },
    rearmDuctedTonnageSeed: (state) => {
      state.ductedFilters.tonnageSeedKey = null;
    },
    // Page one is the rail's first view (leads only); a later page is only
    // ever asked for from the revealed list, so it keeps the reveal.
    setDuctedPagination: (
      state,
      action: PayloadAction<{ pageNumber?: number; pageSize?: number }>
    ) => {
      if (action.payload.pageNumber === 1) {
        resetDuctedPaging(state.ductedFilters);
      } else if (action.payload.pageNumber !== undefined) {
        state.ductedFilters.pageNumber = action.payload.pageNumber;
      }
      if (action.payload.pageSize !== undefined) {
        state.ductedFilters.pageSize = action.payload.pageSize;
        resetDuctedPaging(state.ductedFilters);
      }
    },
    // "Show more" on the rail's first view: the loaded rows beyond each
    // type's lead cards come into view (no fetch — page one is already here).
    revealDuctedTail: (state) => {
      state.ductedFilters.tailRevealed = true;
    },
    // Reset to defaults but keep resetResultsKey: a user Reset already lands
    // on the default empty selection, and preserving the key keeps the
    // once-per-context reset from redispatching right after. The source
    // flags return to 'seed' with the defaults (AHA-6796 EE rows): a Reset
    // surrenders the manual hold, so the NEXT results context re-seeds —
    // within this context nothing re-seeds (the kept key no-ops).
    clearDuctedFilters: (state) => {
      state.ductedFilters = {
        ...defaultDuctedFilters,
        resetResultsKey: state.ductedFilters.resetResultsKey,
        tonnageSeedKey: state.ductedFilters.tonnageSeedKey
      };
    },
    setEquipmentFilters: (
      state,
      action: PayloadAction<EquipmentFilterState>
    ) => {
      state.equipmentFilters = action.payload;
    },
    updateEquipmentFilter: (
      state,
      action: PayloadAction<Partial<EquipmentFilterState>>
    ) => {
      state.equipmentFilters = {
        ...state.equipmentFilters,
        ...action.payload
      };
    },
    setEquipmentFilterMemory: (
      state,
      action: PayloadAction<EquipmentFilterMemoryState>
    ) => {
      state.equipmentFilterMemory = action.payload;
    },
    clearEquipmentFilters: (state) => {
      state.equipmentFilters = defaultEquipmentFilters;
      state.equipmentFilterMemory = defaultEquipmentFilterMemory;
      state.equipmentConfigSelectionSignature = null;
    },
    setEquipmentConfigSelectionSignature: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.equipmentConfigSelectionSignature = action.payload;
    },
    setMatchingAirHandlersMode: (
      state,
      action: PayloadAction<MatchingAirHandlersModeType>
    ) => {
      state.matchingAirHandlersMode = action.payload;
    },
    setGallerySort: (state, action: PayloadAction<GallerySortOption>) => {
      if (state.gallerySort !== action.payload) {
        state.gallerySort = action.payload;
        state.galleryPage = 0;
      }
    },
    // The ducted rail is paged in this order server-side, so a new order is
    // a new result set: back to page one, leads only (a page beyond the
    // first would otherwise be fetched in the new order and appended onto
    // rows of the old one).
    setSystemSort: (state, action: PayloadAction<SystemSortOption>) => {
      state.systemSort = action.payload;
      resetDuctedPaging(state.ductedFilters);
    },
    setGalleryPage: (state, action: PayloadAction<number>) => {
      const next = Math.max(0, action.payload);
      if (state.galleryPage !== next) state.galleryPage = next;
    },
    setGalleryPageSize: (state, action: PayloadAction<number>) => {
      const next = Math.max(1, action.payload);
      if (state.galleryPageSize !== next) {
        state.galleryPageSize = next;
        state.galleryPage = 0;
      }
    },
    setGalleryPagination: (
      state,
      action: PayloadAction<BulkValidSystemsPaginationType | null>
    ) => {
      state.galleryPagination = action.payload;
    },
    setShowSettingsPanel: (state, action: PayloadAction<boolean>) => {
      state.showSettingsPanel = action.payload;
    },
    toggleSettingsPanel: (state) => {
      state.showSettingsPanel = !state.showSettingsPanel;
    },

    setShowChatPanel: (state, action: PayloadAction<boolean>) => {
      state.showChatPanel = action.payload;
    },
    toggleChatPanel: (state) => {
      state.showChatPanel = !state.showChatPanel;
    },
    setShowDebugMenu: (state, action: PayloadAction<boolean>) => {
      state.showDebugMenu = action.payload;
    },
    setPreferredAirHandlerErrors: (
      state,
      action: PayloadAction<{ zoneId: string; error: string }[]>
    ) => {
      state.preferredAirHandlerErrors = action.payload;
    },
    setActiveAirHandlerPicker: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.activeAirHandlerPicker = action.payload;
    },
    setActiveCondenserPicker: (state, action: PayloadAction<string | null>) => {
      state.activeCondenserPicker = action.payload;
    },
    setActiveAccessoryDrawer: (state, action: PayloadAction<string | null>) => {
      state.activeAccessoryDrawer = action.payload;
    },
    openEquipmentModal: (
      state,
      action: PayloadAction<{
        kind: AddEquipmentKind;
        editComponentId?: string;
        targetSystemUniqueId?: string;
      }>
    ) => {
      state.equipmentModalKind = action.payload.kind;
      state.editingEquipmentComponentId =
        action.payload.editComponentId ?? null;
      state.equipmentModalTargetSystemId =
        action.payload.targetSystemUniqueId ?? null;
    },
    closeEquipmentModal: (state) => {
      state.equipmentModalKind = null;
      state.editingEquipmentComponentId = null;
      state.equipmentModalTargetSystemId = null;
    },
    setDebugMode: (state, action: PayloadAction<boolean>) => {
      state.debugMode = action.payload;
    },
    setReproducingErrorLogId: (state, action: PayloadAction<number | null>) => {
      state.reproducingErrorLogId = action.payload;
    },
    setDeveloperMode: (state, action: PayloadAction<boolean>) => {
      state.developerMode = action.payload;
    },
    toggleDeveloperMode: (state) => {
      state.developerMode = !state.developerMode;
    },
    setRebatesForModal: (state, action: PayloadAction<RebateType[]>) => {
      state.rebatesForModal = action.payload;
    },
    setEstimatingStructure: (state, action: PayloadAction<boolean>) => {
      state.estimatingStructure = action.payload;
    },
    setAnalyzingStructureImages: (state, action: PayloadAction<boolean>) => {
      state.analyzingStructureImages = action.payload;
    },
    setEasyMode: (state, action: PayloadAction<boolean>) => {
      state.easyMode = action.payload;
    },
    toggleEasyMode: (state) => {
      state.easyMode = !state.easyMode;
    },
    setShowZones: (state, action: PayloadAction<boolean>) => {
      state.showZones = action.payload;
    },
    toggleShowZones: (state) => {
      state.showZones = !state.showZones;
    },
    setActiveLevelIndex: (state, action: PayloadAction<number>) => {
      state.activeLevelIndex = action.payload;
    },
    setActiveLevelDetailIndex: (state, action: PayloadAction<number>) => {
      state.activeLevelDetailIndex = action.payload;
    },
    setActiveSubStepIndex: (state, action: PayloadAction<number>) => {
      state.activeSubStepIndex = action.payload;
    },
    setEasyModalFinished: (state, action: PayloadAction<EasyModalType>) => {
      state.easyModalsFinished = [
        ...state.easyModalsFinished.filter((m) => m !== action.payload),
        action.payload
      ];
    },
    setComparisonTableData: (
      state,
      action: PayloadAction<ComparisonTableDataType | null>
    ) => {
      state.comparisonTableData = action.payload;
    },
    setGlobalComparisonFeatures: (
      state,
      action: PayloadAction<ComparisonFeatureType[]>
    ) => {
      state.globalComparisonFeatures = action.payload;
    },
    setQuoteIDsToSend: (state, action: PayloadAction<string[]>) => {
      state.quoteIDsToSend = action.payload;
    },
    setLayoutImagesSkipped: (state, action: PayloadAction<boolean>) => {
      state.layoutImagesSkipped = action.payload;
    },
    setSquareFootageWarningShown: (state, action: PayloadAction<boolean>) => {
      state.squareFootageWarningShown = action.payload;
    },
    showTaskModal: (
      state,
      action: PayloadAction<{
        tasks: CompletedTaskType[];
        title?: string;
        flyToTarget?: { x: number; y: number };
      }>
    ) => {
      state.taskModal = {
        isOpen: true,
        tasks: action.payload.tasks,
        title: action.payload.title || 'Completing Tasks',
        flyToTarget: action.payload.flyToTarget || { x: 100, y: 100 }
      };
    },
    hideTaskModal: (state) => {
      state.taskModal.isOpen = false;
    },
    setTaskModalTitle: (state, action: PayloadAction<string>) => {
      state.taskModal.title = action.payload;
    },
    setTaskModalFlyTarget: (
      state,
      action: PayloadAction<{ x: number; y: number }>
    ) => {
      state.taskModal.flyToTarget = action.payload;
    },
    setActiveTipSequence: (
      state,
      action: PayloadAction<ActiveTipSequenceType | null>
    ) => {
      const outgoing = state.activeTipSequence;
      if (outgoing?.dismissKey) {
        state.tipSequenceProgress[outgoing.dismissKey] = outgoing.tipIndex;
      }
      if (!action.payload) {
        state.activeTipSequence = null;
        return;
      }
      const savedTipIndex = action.payload.dismissKey
        ? state.tipSequenceProgress[action.payload.dismissKey] ?? 0
        : 0;
      const tipIndex = Math.max(
        Math.min(savedTipIndex, action.payload.tips.length - 1),
        0
      );
      state.activeTipSequence = { ...action.payload, tipIndex };
    },
    refreshActiveTipSequence: (
      state,
      action: PayloadAction<ActiveTipSequenceType>
    ) => {
      if (!state.activeTipSequence) return;
      const tipIndex = Math.min(
        state.activeTipSequence.tipIndex,
        action.payload.tips.length - 1
      );
      state.activeTipSequence = { ...action.payload, tipIndex };
    },
    nextTip: (state) => {
      if (!state.activeTipSequence) return;
      const lastIndex = state.activeTipSequence.tips.length - 1;
      state.activeTipSequence.tipIndex = Math.min(
        state.activeTipSequence.tipIndex + 1,
        lastIndex
      );
    },
    previousTip: (state) => {
      if (!state.activeTipSequence) return;
      state.activeTipSequence.tipIndex = Math.max(
        state.activeTipSequence.tipIndex - 1,
        0
      );
    },
    dismissActiveTip: (state) => {
      const dismissKey = state.activeTipSequence?.dismissKey;
      if (dismissKey) {
        if (!state.dismissedTips.includes(dismissKey)) {
          state.dismissedTips.push(dismissKey);
        }
        delete state.tipSequenceProgress[dismissKey];
      }
      state.activeTipSequence = null;
    },
    skipAllTips: (state) => {
      state.tipsSkipped = true;
      state.activeTipSequence = null;
      state.tipSequenceProgress = {};
    },
    resetTips: (state) => {
      state.dismissedTips = [];
      state.tipsSkipped = false;
      state.activeTipSequence = null;
      state.tipSequenceProgress = {};
    },
    addManuallyCompletedStep: (state, action: PayloadAction<string>) => {
      if (!state.manuallyCompletedSteps.includes(action.payload)) {
        state.manuallyCompletedSteps.push(action.payload);
      }
    },
    addVisitedStructureStep: (state, action: PayloadAction<string>) => {
      if (!state.visitedStructureSteps.includes(action.payload)) {
        state.visitedStructureSteps.push(action.payload);
      }
    },
    removeManuallyCompletedStep: (state, action: PayloadAction<string>) => {
      state.manuallyCompletedSteps = state.manuallyCompletedSteps.filter(
        (step) => step !== action.payload
      );
    },
    setCompletedStepsFromDatabase: (state, action: PayloadAction<string[]>) => {
      state.manuallyCompletedSteps = action.payload;
    },
    setCompletedStepsLoaded: (state, action: PayloadAction<boolean>) => {
      state.completedStepsLoaded = action.payload;
    },
    setPendingImportCompletedSteps: (
      state,
      action: PayloadAction<StructureStepRef[] | null>
    ) => {
      state.pendingImportCompletedSteps = action.payload;
    },
    resetManuallyCompletedSteps: (state) => {
      state.manuallyCompletedSteps = [];
      state.visitedStructureSteps = [];
      state.completedStepsLoaded = false;
    },
    setSkipConfigurationReset: (state, action: PayloadAction<boolean>) => {
      state.skipConfigurationReset = action.payload;
    },
    setCategoriesWithUnselectedRecommendations: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.categoriesWithUnselectedRecommendations = action.payload;
    },
    setActiveStructureStepId: (state, action: PayloadAction<string>) => {
      state.activeStructureStepId = action.payload;
    },
    setActiveProjectStepId: (state, action: PayloadAction<string>) => {
      state.activeProjectStepId = action.payload;
    },
    setActiveExistingEquipmentStepId: (
      state,
      action: PayloadAction<string>
    ) => {
      state.activeExistingEquipmentStepId = action.payload;
    },
    setActiveConfigurationStepId: (state, action: PayloadAction<string>) => {
      state.activeConfigurationStepId = action.payload;
    },
    setActiveConfigurationStepConfigId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.activeConfigurationStepConfigId = action.payload;
    },
    addFetchingCondenserIds: (state, action: PayloadAction<string[]>) => {
      const existing = new Set(state.fetchingCondenserIds);
      action.payload.forEach((id) => existing.add(id));
      state.fetchingCondenserIds = Array.from(existing);
    },
    removeFetchingCondenserIds: (state, action: PayloadAction<string[]>) => {
      if (action.payload.length === 0) return;
      const removing = new Set(action.payload);
      state.fetchingCondenserIds = state.fetchingCondenserIds.filter(
        (id) => !removing.has(id)
      );
    },
    clearFetchingCondenserIds: (state) => {
      if (state.fetchingCondenserIds.length === 0) return;
      state.fetchingCondenserIds = [];
    },
    addFetchingFullConfigIds: (state, action: PayloadAction<string[]>) => {
      const existing = new Set(state.fetchingFullConfigIds);
      action.payload.forEach((id) => existing.add(id));
      state.fetchingFullConfigIds = Array.from(existing);
    },
    removeFetchingFullConfigIds: (state, action: PayloadAction<string[]>) => {
      if (action.payload.length === 0) return;
      const removing = new Set(action.payload);
      state.fetchingFullConfigIds = state.fetchingFullConfigIds.filter(
        (id) => !removing.has(id)
      );
    },
    addFailedValidSystemsConfigId: (
      state,
      action: PayloadAction<{
        configId: string;
        source: FailedValidSystemsSource;
      }>
    ) => {
      const { configId, source } = action.payload;
      const existing = state.failedValidSystemsConfigSources[configId] ?? [];
      if (!existing.includes(source)) {
        state.failedValidSystemsConfigSources[configId] = [...existing, source];
      }
    },
    removeFailedValidSystemsConfigId: (
      state,
      action: PayloadAction<{
        configId: string;
        source: FailedValidSystemsSource;
      }>
    ) => {
      const { configId, source } = action.payload;
      const existing = state.failedValidSystemsConfigSources[configId];
      if (!existing) return;
      const next = existing.filter((s) => s !== source);
      if (next.length === 0) {
        delete state.failedValidSystemsConfigSources[configId];
      } else {
        state.failedValidSystemsConfigSources[configId] = next;
      }
    },
    removeAllFailedValidSystemsConfigIdSources: (
      state,
      action: PayloadAction<string>
    ) => {
      if (state.failedValidSystemsConfigSources[action.payload]) {
        delete state.failedValidSystemsConfigSources[action.payload];
      }
    },
    setZoneDuctMode: (
      state,
      action: PayloadAction<{
        zoneUniqueId: string;
        modeKey: RadialDuctModeKeyType;
      }>
    ) => {
      state.zoneDuctModes[action.payload.zoneUniqueId] = action.payload.modeKey;
    },
    setZoneDuctModes: (state, action: PayloadAction<ZoneDuctModesMapType>) => {
      state.zoneDuctModes = action.payload;
    },
    setConfidenceGateTelemetry: (
      state,
      action: PayloadAction<ConfidenceGateTelemetryType | null>
    ) => {
      state.confidenceGateTelemetry = action.payload;
    },
    setHoveredConcealedZone: (state, action: PayloadAction<string | null>) => {
      state.hoveredConcealedZoneId = action.payload;
    },
    setDuctedPairPick: (
      state,
      action: PayloadAction<{ systemUniqueId: string; pick: DuctedPairPick }>
    ) => {
      state.ductedPairPicks[action.payload.systemUniqueId] =
        action.payload.pick;
    },
    clearDuctedPairPick: (state, action: PayloadAction<string>) => {
      delete state.ductedPairPicks[action.payload];
    },
    clearDuctedPairPicks: (state) => {
      state.ductedPairPicks = {};
    }
  }
});

export const {
  setActiveTab,
  setActiveAdminTab,
  setPageProgress,
  setActiveItem,
  setShowPropertySummary,
  setGoogleApiLoaded,
  setSliderValues,
  setDefaultSliderValues,
  setZoneSliderValues,
  setSliderRanges,
  setSystemFilters,
  setGalleryFilters,
  updateGalleryFilter,
  setDuctedCombinationFilters,
  setDuctedSystemTypeSelections,
  clearDuctedChassisFilter,
  resetDuctedSystemTypeCodes,
  setDuctedFuelSelections,
  setDuctedEquipmentMatchingOnly,
  setDuctedApplySizingBands,
  applyDuctedTonnageSeed,
  rearmDuctedTonnageSeed,
  setDuctedPagination,
  revealDuctedTail,
  clearDuctedFilters,
  setEquipmentFilters,
  updateEquipmentFilter,
  setEquipmentFilterMemory,
  clearEquipmentFilters,
  setEquipmentConfigSelectionSignature,
  setMatchingAirHandlersMode,
  setGallerySort,
  setSystemSort,
  setGalleryPage,
  setGalleryPageSize,
  setGalleryPagination,
  setShowSettingsPanel,
  toggleSettingsPanel,

  setShowChatPanel,
  toggleChatPanel,
  setShowDebugMenu,
  setPreferredAirHandlerErrors,
  setActiveAirHandlerPicker,
  setActiveCondenserPicker,
  setActiveAccessoryDrawer,
  openEquipmentModal,
  closeEquipmentModal,
  setDebugMode,
  setReproducingErrorLogId,
  setDeveloperMode,
  toggleDeveloperMode,
  setRebatesForModal,
  setEstimatingStructure,
  setAnalyzingStructureImages,
  setEasyMode,
  toggleEasyMode,
  setShowZones,
  toggleShowZones,
  setActiveLevelIndex,
  setActiveLevelDetailIndex,
  setActiveSubStepIndex,
  setEasyModalFinished,
  setComparisonTableData,
  setGlobalComparisonFeatures,
  setQuoteIDsToSend,
  setLayoutImagesSkipped,
  setSquareFootageWarningShown,
  showTaskModal,
  hideTaskModal,
  setTaskModalTitle,
  setTaskModalFlyTarget,
  setActiveTipSequence,
  refreshActiveTipSequence,
  nextTip,
  previousTip,
  dismissActiveTip,
  skipAllTips,
  resetTips,
  addManuallyCompletedStep,
  addVisitedStructureStep,
  removeManuallyCompletedStep,
  setCompletedStepsFromDatabase,
  setCompletedStepsLoaded,
  setPendingImportCompletedSteps,
  resetManuallyCompletedSteps,
  setSkipConfigurationReset,
  setCategoriesWithUnselectedRecommendations,
  setActiveStructureStepId,
  setActiveProjectStepId,
  setActiveExistingEquipmentStepId,
  setActiveConfigurationStepId,
  setActiveConfigurationStepConfigId,
  addFetchingCondenserIds,
  removeFetchingCondenserIds,
  clearFetchingCondenserIds,
  addFetchingFullConfigIds,
  removeFetchingFullConfigIds,
  addFailedValidSystemsConfigId,
  removeFailedValidSystemsConfigId,
  removeAllFailedValidSystemsConfigIdSources,
  setZoneDuctMode,
  setZoneDuctModes,
  setConfidenceGateTelemetry,
  setHoveredConcealedZone,
  setDuctedPairPick,
  clearDuctedPairPick,
  clearDuctedPairPicks
} = uiSlice.actions;

export default uiSlice.reducer;
